<!--
 * @Author: 张天昊
 * @Date: 2024-05-21 17:43:57
 * @LastEditTime: 2024-07-11 14:27:12
 * @LastEditors: 张天昊
 * @Description: 旅行社卡片
 * @FilePath: /Mobile/src/pages/itineraryUniversal/components/TravelAgencyCard.vue
-->
<script>
import { defineComponent, computed } from 'vue';
import CardContainer from './CardContainer.vue';
import Description from './Description.vue';
import WithPhone from './WithPhone.vue';
import WithLink from './WithLink.vue';
import { useCardContainerEl } from '../composables/useCardContainerEl';
import { useAuth } from '../composables/useAuth';
import { TravelAgencyTypes } from '../constants';
import { genEmptyTextPlaceholder, formatPriceValue } from '../utils';
import { navigateToItineraryUniversal } from '@/router/navigate';
import imgTravelAgencyPlaceholder from '@/assets/itineraryUniversal/travel-agency-placeholder.png';
import store from '@/store';
import router from '@/router/routers';

export default defineComponent({
  components: {
    CardContainer,
    Description,
    WithPhone,
    WithLink
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props, { expose }) {
    const { container } = useCardContainerEl();
    const { travelAgencyDetailAuth } = useAuth();
    const detailInfo = computed(
      () => store.state.itineraryUniversal.detailInfo.value
    );
    const TravelAgencyTypesEntries = Object.entries(TravelAgencyTypes);
    const computedType = computed(() => {
      const res = TravelAgencyTypesEntries.find(
        ([label, value]) => props.data.type === value
      );
      if (res) {
        return res[0];
      }
      return '';
    });
    const computedRelatedMainId = computed(
      () => detailInfo.value?.itineraryInfoMessage.mainTeamId
    );
    const computedRelatedChildId = computed(() => props.data.childTeamId);
    const showDetailLink = computed(() => {
      return (
        props.data.type !== TravelAgencyTypes.省外组团社 &&
        travelAgencyDetailAuth.value &&
        props.data.travelId
      );
    });

    function onClick() {
      if (travelAgencyDetailAuth.value && props.data.travelId) {
        // 跳转旅行社详情
        window.yyn.invokeJSB('openWindow', {
          openUrl: `${window.location.origin}/web/#/travelAgency/info?id=${props.data.travelId}`,
          isFullScreen: 0,
          needLoading: 1
        });
      }
    }

    function handleNavigateOtherDetail(id) {
      navigateToItineraryUniversal(id, router.currentRoute.query.scene);
    }

    expose({
      // 站点服务接口没有返旅行社id，用名称代替
      bid: props.data.travelId || props.data.travelName,
      toggleHighlight: () => {
        if (container.value) {
          container.value.toggleHighlight();
        }
      }
    });
    return {
      imgTravelAgencyPlaceholder,
      travelAgencyDetailAuth,
      TravelAgencyTypes,
      showDetailLink,
      computedType,
      computedRelatedMainId,
      computedRelatedChildId,
      container,
      onClick,
      genEmptyTextPlaceholder,
      formatPriceValue,
      handleNavigateOtherDetail
    };
  }
});
</script>

<template>
  <CardContainer ref="container" bordered @click="onClick">
    <div class="relative">
      <div class="flex items-center">
        <van-image
          :width="$px2rem(48)"
          :height="$px2rem(48)"
          :radius="$px2rem(4)"
          :error-icon="imgTravelAgencyPlaceholder"
          :src="data.photoPath || ''"
          class="flex-shrink-0 custom-avatar"
          fit="cover"
        />
        <div class="ml-13 text-18 leading-24 font-semibold">
          {{ data.name }}
        </div>
      </div>
      <van-divider class="custom-divider" />
      <template v-if="data.type === TravelAgencyTypes.省外组团社">
        <Description
          class="mt-8"
          label="统一社会信用代码"
          :content="genEmptyTextPlaceholder(data.businessLicense)"
        />
        <Description
          class="mt-8"
          label="经营许可证编号"
          :content="genEmptyTextPlaceholder(data.businessLicenseNumber)"
        />
        <Description
          class="mt-8"
          label="旅行社类型"
          :content="genEmptyTextPlaceholder(computedType)"
        />
        <Description
          class="mt-8"
          label="计调"
          :content="genEmptyTextPlaceholder(data.practitionerName)"
        />
        <Description class="mt-8" label="联系电话">
          <WithPhone
            :phone-number="data.practitionerPhone"
            class="text-black-a"
          />
        </Description>
        <Description
          class="mt-8"
          label="委托价格"
          :content="formatPriceValue(data.precatoryPrice)"
        />
      </template>
      <template v-else-if="data.type === TravelAgencyTypes.组团社">
        <Description
          class="mt-8"
          label="自编团号"
          :content="genEmptyTextPlaceholder(data.selfTeamNo)"
        />
        <Description
          class="mt-8"
          label="计调"
          :content="genEmptyTextPlaceholder(data.practitionerName)"
        />
        <Description
          v-if="computedRelatedMainId"
          class="mt-8"
          label="委托主单号"
        >
          <WithLink @click="handleNavigateOtherDetail(computedRelatedMainId)">
            {{ computedRelatedMainId }}
            <template #link>
              <span class="text-green-primary ml-6 inline-flex items-center"
                >查看<svg-icon
                  pre-path="itineraryUniversal"
                  name="ic-arrow"
                  class="stroke-green-primary w-12 h-12"
              /></span>
            </template>
          </WithLink>
        </Description>
        <Description class="mt-8" label="联系电话">
          <WithPhone
            :phone-number="data.practitionerPhone"
            class="text-black-a"
          />
        </Description>
      </template>
      <template v-else-if="data.type === TravelAgencyTypes.地接社">
        <Description
          class="mt-8"
          label="计调"
          :content="genEmptyTextPlaceholder(data.practitionerName)"
        />
        <Description
          class="mt-8"
          label="委托价格"
          :content="formatPriceValue(data.precatoryPrice)"
        />
        <Description
          v-if="computedRelatedChildId"
          class="mt-8"
          label="关联子单号"
        >
          <WithLink @click="handleNavigateOtherDetail(computedRelatedChildId)">
            {{ computedRelatedChildId }}
            <template #link>
              <span class="text-green-primary ml-6 inline-flex items-center"
                >查看<svg-icon
                  pre-path="itineraryUniversal"
                  name="ic-arrow"
                  class="stroke-green-primary w-12 h-12"
              /></span>
            </template>
          </WithLink>
        </Description>
        <Description class="mt-8" label="联系电话">
          <WithPhone
            :phone-number="data.practitionerPhone"
            class="text-black-a"
          />
        </Description>
      </template>
      <template v-if="showDetailLink">
        <van-divider class="custom-divider-footer" />
        <div
          class="pt-16 rounded-bl-4 rounded-br-4 flex items-center justify-center text-14 leading-22 font-semibold text-green-primary van-haptics-feedback"
        >
          旅行社详情<svg-icon
            pre-path="itineraryUniversal"
            name="ic-arrow"
            class="stroke-green-primary w-16 h-16"
          />
        </div>
      </template>
    </div>
  </CardContainer>
</template>

<style lang="less" scoped>
.custom-divider {
  margin: 14px 0 10px;
}
.custom-divider-footer {
  margin: 16px 0 0;
}
</style>
